import { useState, useEffect } from 'react';
import { Plus, RefreshCw, FileText, Download, Upload, Trash2, Tag, Edit2, File, Search, AlertTriangle } from 'lucide-react';
import api from '../api/api';

const CATEGORIES = {
  authorization: { l: 'Autorización Ambiental', cls: 'bg-green-100 text-green-700' },
  procedure: { l: 'Procedimiento', cls: 'bg-blue-100 text-blue-700' },
  certificate: { l: 'Certificado', cls: 'bg-purple-100 text-purple-700' },
  manual: { l: 'Manual', cls: 'bg-yellow-100 text-yellow-800' },
  report: { l: 'Informe', cls: 'bg-orange-100 text-orange-700' },
  insurance: { l: 'Seguro', cls: 'bg-red-100 text-red-700' },
  calibration: { l: 'Calibración', cls: 'bg-cyan-100 text-cyan-700' },
  safety_plan: { l: 'Plan de Seguridad', cls: 'bg-rose-100 text-rose-700' },
  technical_sheet: { l: 'Ficha Técnica', cls: 'bg-indigo-100 text-indigo-700' },
  regulation: { l: 'Normativa', cls: 'bg-teal-100 text-teal-700' },
  other: { l: 'Otro', cls: 'bg-gray-100 text-gray-600' },
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Documentos() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: '', category: 'procedure', description: '', file_url: '', valid_from: '', valid_until: '', tags: '' });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/documents');
      setDocs(res.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = docs.filter(d => {
    const matchSearch = !search || d.title.toLowerCase().includes(search.toLowerCase()) || d.description?.toLowerCase().includes(search.toLowerCase()) || d.tags?.some(t => t.toLowerCase().includes(search.toLowerCase()));
    const matchCat = !catFilter || d.category === catFilter;
    return matchSearch && matchCat;
  });

  const openCreate = () => { setEditDoc(null); setForm({ title: '', category: 'procedure', description: '', file_url: '', valid_from: '', valid_until: '', tags: '' }); setShowModal(true); };
  const openEdit = (doc) => { setEditDoc(doc); setForm({ ...doc, tags: doc.tags?.join(', ') || '' }); setShowModal(true); };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const data = { ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) };
      if (editDoc) { await api.put(`/documents/${editDoc.id}`, data); }
      else { await api.post('/documents', data); }
      setShowModal(false); load();
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    await api.delete(`/documents/${id}`); load();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, file_url: `${API_URL}${res.data.url}`, title: f.title || res.data.filename }));
    } finally { setUploading(false); }
  };

  const isExpiringSoon = (valid_until) => {
    if (!valid_until) return false;
    const diff = (new Date(valid_until) - new Date()) / (1000 * 60 * 60 * 24);
    return diff < 90 && diff > 0;
  };
  const isExpired = (valid_until) => {
    if (!valid_until) return false;
    return new Date(valid_until) < new Date();
  };

  return (
    <div className="p-6 md:p-8 page-enter">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Gestión Documental</h1>
          <p className="text-sm text-gray-500 mt-0.5">{docs.length} documentos · Normativa, certificados, manuales y más</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500"><RefreshCw size={16} /></button>
          <button onClick={openCreate} className="flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-medium px-4 py-2 rounded-lg" data-testid="add-doc-btn"><Plus size={16} /> Nuevo Documento</button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {Object.entries(CATEGORIES).slice(0, 4).map(([k, v]) => {
          const count = docs.filter(d => d.category === k).length;
          return <div key={k} className="bg-white border border-gray-200 rounded-xl p-3 flex items-center gap-3 shadow-sm">
            <FileText size={16} className="text-gray-400 flex-shrink-0" />
            <div><p className="text-xs text-gray-500">{v.l}</p><p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>{count}</p></div>
          </div>;
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar documentos..." className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none" />
        </div>
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-[#16a34a] focus:outline-none">
          <option value="">Todas las categorías</option>
          {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}
        </select>
      </div>

      {/* Document grid */}
      {loading ? <div className="flex justify-center h-40 items-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#16a34a]" /></div>
        : filtered.length === 0
          ? <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">Sin documentos{search ? ' que coincidan con la búsqueda' : ''}</div>
          : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map(doc => {
                const cat = CATEGORIES[doc.category] || CATEGORIES.other;
                const expired = isExpired(doc.valid_until);
                const expiring = isExpiringSoon(doc.valid_until);
                return (
                  <div key={doc.id} className={`bg-white rounded-xl border shadow-sm p-5 flex flex-col gap-3 transition-shadow hover:shadow-md ${expired ? 'border-red-300' : expiring ? 'border-yellow-300' : 'border-gray-200'}`} data-testid={`doc-card-${doc.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="p-2 bg-gray-100 rounded-lg"><File size={18} className="text-gray-600" /></div>
                      <div className="flex gap-1 flex-shrink-0">
                        {expired && <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium flex items-center gap-0.5"><AlertTriangle size={9} />Caducado</span>}
                        {expiring && !expired && <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded-full font-medium">Próximo vencer</span>}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-bold text-gray-900 leading-snug mb-1">{doc.title}</h3>
                      {doc.description && <p className="text-xs text-gray-500 line-clamp-2">{doc.description}</p>}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${cat.cls}`}>{cat.l}</span>
                      {doc.tags?.slice(0, 2).map(t => <span key={t} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">{t}</span>)}
                    </div>
                    {(doc.valid_from || doc.valid_until) && (
                      <p className="text-[11px] text-gray-400">
                        {doc.valid_from && `Desde: ${doc.valid_from}`}
                        {doc.valid_from && doc.valid_until && ' · '}
                        {doc.valid_until && <span className={expired ? 'text-red-500 font-medium' : expiring ? 'text-yellow-600 font-medium' : ''}>Hasta: {doc.valid_until}</span>}
                      </p>
                    )}
                    <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                      {doc.file_url
                        ? <a href={doc.file_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"><Download size={13} />Descargar</a>
                        : <span className="text-xs text-gray-300">Sin archivo adjunto</span>}
                      <div className="ml-auto flex gap-1">
                        <button onClick={() => openEdit(doc)} className="p-1.5 text-gray-400 hover:text-blue-600 rounded" data-testid={`edit-doc-${doc.id}`}><Edit2 size={13} /></button>
                        <button onClick={() => handleDelete(doc.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded" data-testid={`delete-doc-${doc.id}`}><Trash2 size={13} /></button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10">
              <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>{editDoc ? 'Editar Documento' : 'Nuevo Documento'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <form onSubmit={handleSave} className="p-5 space-y-4">
              <div><label className="block text-xs font-medium text-gray-700 mb-1">Título *</label>
                <input type="text" required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none" /></div>
              <div><label className="block text-xs font-medium text-gray-700 mb-1">Categoría</label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none bg-white">
                  {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}
                </select></div>
              <div><label className="block text-xs font-medium text-gray-700 mb-1">Descripción</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none resize-none" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-xs font-medium text-gray-700 mb-1">Válido desde</label>
                  <input type="date" value={form.valid_from} onChange={e => setForm({ ...form, valid_from: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none" /></div>
                <div><label className="block text-xs font-medium text-gray-700 mb-1">Válido hasta</label>
                  <input type="date" value={form.valid_until} onChange={e => setForm({ ...form, valid_until: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none" /></div>
              </div>
              <div><label className="block text-xs font-medium text-gray-700 mb-1">Etiquetas (separadas por coma)</label>
                <input type="text" value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="AAI, medioambiente, autorización..." className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none" /></div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Archivo adjunto</label>
                <div className="flex gap-2">
                  <input type="text" value={form.file_url} onChange={e => setForm({ ...form, file_url: e.target.value })} placeholder="URL del documento o sube uno..." className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none" />
                  <label className={`flex items-center gap-1 px-3 py-2 text-xs border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 whitespace-nowrap ${uploading ? 'opacity-50' : ''}`}>
                    <Upload size={13} />{uploading ? 'Subiendo...' : 'Subir'}
                    <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploading} accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt" />
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-[#16a34a] text-white rounded-lg hover:bg-[#15803d]" data-testid="save-doc-btn">{saving ? 'Guardando...' : 'Guardar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
